import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import NavigatorManageTable, {
  OverflowCell,
  nameSort,
  SimpleActionsMenu,
} from './NavigatorManageTable';

const SEARCH_KEYS = ['name'];

const BULK_ACTIONS = [
  {
    key: 'deleteGroups', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const UserCountLink = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 4px;
  color: #77A7FF;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(119, 167, 255, 0.2);
  }
`;

// Jumps to Manage Athletes pre-filtered to this group, matching Manage Calendars' Users link.
const UserCountCell = ({ row }) => {
  const history = useHistory();
  return (
    <UserCountLink
      onClick={(e) => {
        e.stopPropagation();
        history.push('/dashboard/manage-athletes', {
          filterGroupId: row.original.id,
          backPath: '/dashboard/manage-groups',
          backLabel: 'Back to Manage Groups',
        });
      }}
    >
      {row.original.userCount ?? 0}
    </UserCountLink>
  );
};

UserCountCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      userCount: PropTypes.number,
    }),
  }).isRequired,
};

const ActionDropdownCell = ({ row }) => {
  const group = row.original;
  return (
    <SimpleActionsMenu onEdit={() => group.onEdit(group)} onDelete={() => group.onDelete(group)} />
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const ManageGroupsTable = ({
  onEditGroup, onDeleteGroup, onBulkAction, onVisibleGroupsChange,
}) => {
  const groups = useSelector((state) => state.navigator.data.groups);
  const isLoadingGroups = useSelector((state) => state.navigator.ui.isLoadingGroups);

  const tableData = useMemo(
    () => groups.map((group) => ({
      ...group,
      onEdit: onEditGroup,
      onDelete: onDeleteGroup,
    })),
    [groups, onEditGroup, onDeleteGroup],
  );

  const columns = useMemo(() => [
    {
      Header: 'Group Name',
      accessor: 'name',
      sortType: nameSort,
      headerStyle: { minWidth: '250px', flex: '1' },
      cellStyle: { minWidth: '250px', flex: '1', overflow: 'hidden' },
      Cell: OverflowCell,
    },
    {
      Header: 'Messaging',
      id: 'messaging',
      accessor: (row) => (row.groupFilters?.messaging ? 'Yes' : 'No'),
      headerStyle: { width: '140px', textAlign: 'left' },
      cellStyle: { width: '140px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'Leaderboard',
      id: 'leaderboard',
      accessor: (row) => (row.groupFilters?.leaderboard ? 'Yes' : 'No'),
      headerStyle: { width: '140px', textAlign: 'left' },
      cellStyle: { width: '140px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'Reporting',
      id: 'reporting',
      accessor: (row) => (row.groupFilters?.reporting ? 'Yes' : 'No'),
      headerStyle: { width: '140px', textAlign: 'left' },
      cellStyle: { width: '140px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: '# of Users',
      accessor: 'userCount',
      headerStyle: { width: '140px', textAlign: 'left' },
      cellStyle: { width: '140px', textAlign: 'left' },
      Cell: UserCountCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '100px', textAlign: 'center' },
      cellStyle: { width: '100px', textAlign: 'center' },
      Cell: ActionDropdownCell,
    },
  ], []);

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoadingGroups}
      searchKeys={SEARCH_KEYS}
      searchPlaceholder='Search for a group'
      emptyMessage='No groups found.'
      searchEmptyMessage='No groups match your search.'
      entityLabel='group'
      defaultSortBy={[{ id: 'name', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={onBulkAction}
      onFilteredDataChange={onVisibleGroupsChange}
    />
  );
};

ManageGroupsTable.propTypes = {
  onEditGroup: PropTypes.func,
  onDeleteGroup: PropTypes.func,
  onBulkAction: PropTypes.func.isRequired,
  onVisibleGroupsChange: PropTypes.func,
};

ManageGroupsTable.defaultProps = {
  onEditGroup: () => {},
  onDeleteGroup: () => {},
  onVisibleGroupsChange: () => {},
};

export default ManageGroupsTable;
